"use client";

import { useEffect, useState, useCallback } from "react";
import type { User } from "@supabase/supabase-js";
import type { UserProfile } from "@/lib/users/types";
import type { ActivityCounts } from "@/lib/auth/context";
import type { OrgMembership } from "@/lib/organizations/types";
export interface UseAuthProfileReturn {
  userProfile: UserProfile | null;
  isProfileCheckDone: boolean;
  activityCounts: ActivityCounts | null;
  organization: OrgMembership | null;
  refetchProfile: () => Promise<void>;
  /** Reset all profile/org state (on sign-out) */
  resetProfile: () => void;
}

/**
 * Fetches and manages the user profile, activity counts, and organization membership.
 * Runs after the auth session resolves.
 */
export function useAuthProfile(user: User | null, isLoading: boolean): UseAuthProfileReturn {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isProfileCheckDone, setIsProfileCheckDone] = useState(false);
  const [activityCounts, setActivityCounts] = useState<ActivityCounts | null>(null);
  const [organization, setOrganization] = useState<OrgMembership | null>(null);

  // Fetch user profile after auth resolves
  useEffect(() => {
    if (isLoading) return;
    if (!user || user.is_anonymous) {
      // No profile to fetch — mark check done in a microtask to avoid
      // synchronous setState in effect body (react-hooks/set-state-in-effect).
      queueMicrotask(() => setIsProfileCheckDone(true));
      return;
    }
    const fetchProfile = async () => {
      try {
        const response = await fetch("/api/user/profile");
        if (response.ok) {
          const data = await response.json();
          setUserProfile(data.profile);
          if (data.activity_counts) {
            setActivityCounts(data.activity_counts);
          }
        }
      } catch {
        /* ignore */
      }
      setIsProfileCheckDone(true);
    };
    fetchProfile();
  }, [user, isLoading]);

  // Fetch organization membership after auth resolves
  useEffect(() => {
    if (isLoading) return;
    if (!user || user.is_anonymous) {
      queueMicrotask(() => setOrganization(null));
      return;
    }
    const fetchOrg = async () => {
      try {
        const response_0 = await fetch("/api/tenant");
        if (response_0.ok) {
          const data_0 = await response_0.json();
          setOrganization(data_0.organization ?? null);
        }
      } catch {
        /* ignore */
      }
    };
    fetchOrg();
  }, [user, isLoading]);

  // Refetch profile (and activity counts)
  const refetchProfile = useCallback(async () => {
    try {
      const response_1 = await fetch("/api/user/profile");
      if (response_1.ok) {
        const data_1 = await response_1.json();
        setUserProfile(data_1.profile);
        if (data_1.activity_counts) {
          setActivityCounts(data_1.activity_counts);
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Reset all state (called on sign-out)
  const resetProfile = useCallback(() => {
    setUserProfile(null);
    setIsProfileCheckDone(false);
    setActivityCounts(null);
    setOrganization(null);
  }, []);
  return {
    userProfile,
    isProfileCheckDone,
    activityCounts,
    organization,
    refetchProfile,
    resetProfile
  };
}